/**
 * PublicLeagueNewsPage — /l/:slug/news
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). The league's public blog —
 * every published league-wide post (manager-authored), newest first,
 * with load-more paging via the ?before cursor.
 */

import { useEffect, useState, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { container, Section, EmptyState, MUTED } from './pubUi.jsx';
import { NewsCard } from './PublicLeagueHomePage.jsx';

export default function PublicLeagueNewsPage() {
  const { slug } = useParams();
  const { league, accent } = useOutletContext();
  const [posts, setPosts] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (league?.name) document.title = `News — ${league.name}`;
  }, [league?.name]);

  const loadFirst = useCallback(async () => {
    try {
      const { data } = await publicApi.get(`/leagues/${encodeURIComponent(slug)}/posts?limit=24`);
      const list = data?.posts || [];
      setPosts(list);
      setDone(list.length < 24);
    } catch {
      setPosts([]);
      setDone(true);
    }
  }, [slug]);
  useEffect(() => { loadFirst(); }, [loadFirst]);

  async function loadMore() {
    if (!posts?.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const before = posts[posts.length - 1].created_at;
      const { data } = await publicApi.get(
        `/leagues/${encodeURIComponent(slug)}/posts?limit=24&before=${encodeURIComponent(before)}`);
      const more = data?.posts || [];
      setPosts(prev => [...prev, ...more]);
      if (more.length < 24) setDone(true);
    } catch {
      setDone(true);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section eyebrow="From the league office" title="League news">
        {posts === null && <div style={{ color: MUTED }}>Loading…</div>}
        {posts?.length === 0 && (
          <EmptyState icon="📰">Nothing posted yet — the league office will share updates here.</EmptyState>
        )}
        {posts?.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 18 }}>
              {posts.map(p => <NewsCard key={p.id} slug={slug} post={p} accent={accent} />)}
            </div>
            {!done && (
              <div style={{ textAlign: 'center', marginTop: 26 }}>
                <button type="button" onClick={loadMore} disabled={loadingMore}
                  style={{
                    padding: '11px 24px', borderRadius: 12, border: `2px solid ${accent}`,
                    background: 'transparent', color: accent, fontWeight: 800,
                    fontSize: 14, cursor: 'pointer',
                  }}>
                  {loadingMore ? 'Loading…' : 'Load older posts'}
                </button>
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
